import { type ComponentPublicInstance } from 'vue'
import { alertModal } from '@shared/utils/misc/modal'
import { app as appService, errorHandler } from '@/services'
import { reducedErrorReporting } from '@/services/errorHandler'
import ErrorReport from '@/services/ResponseErrorComponent.vue'

// Setup error handler and loading interceptors.
errorHandler.setErrorHandler((title: string, message: string) => {
  return alertModal(title, message)
})
errorHandler.setErrorResultHandler(appService.errorResult)

// NB: This is so evil it fell out of the evil tree and hit every branch on the way down.
//     Yes, it sucks, but the ui recovers from this error and so showing it just is kind
//     of tacky and serves no purpose.
//
//     These errors happen when the component model "shakes" during navigation, where
//     components are being rendered/removed in an asynchronous way, and the unhooking
//     of said components chases its parent pointer only to find an ancestor component
//     has already unhooked.
const suppressedErrors = /Cannot read properties of null \(reading 'parentNode'\)|Cannot destructure property 'bum' of 'f' as it is null/
const isSuppressedError = (err: Error): boolean => {
  const dev = import.meta.env.MODE === 'development'
  if (dev) {
    return false // Always show in dev mode
  }

  return !!err.message.match(suppressedErrors)
}

function getComponentTrace(vm: any): string[] {
  if (!vm || !vm.$options) { return ['Unknown Component'] }

  try {
    const trace: string[] = []
    const visited = new Set()

    while (vm && !visited.has(vm)) {
      visited.add(vm)
      const name = vm.$options?.name || vm.$options?._componentTag || 'Unknown Component'
      let elementInfo = ''
      if (vm.$el) {
        const tag = vm.$el.tagName?.toLowerCase() || vm.$el.nodeName?.toLowerCase()
        const id = vm.$el.id ? ` id:${vm.$el.id}` : ''
        const did =
          (typeof vm.$el.getAttribute === 'function' && (vm.$el.getAttribute('data-test-id') || vm.$el.getAttribute('dataTestId'))) ||
          (typeof vm.$props === 'object' && (vm.$props['data-test-id'] || vm.$props.dataTestId)) ||
          ''
        const dataTestId = did ? ` did:${did}` : ''
        const classes = vm.$el.className ? ` .${vm.$el.className.replace(/\s+/g, '.')}` : ''

        elementInfo = ` (DOM: [${tag}${id}${dataTestId}${classes}])`
      }
      trace.push(`${name}${elementInfo}`)
      vm = vm.$parent
    }

    return trace.reverse().map((c, i) => ' '.repeat(i) + ` ${c}`)
  } catch (e) {
    console.error(`Error while generating component trace: ${String(e)}`)
    return []
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const appErrorHandler = (error: unknown, vm: ComponentPublicInstance | null, info: string) => {
    const reportWarningOnly = reducedErrorReporting()

    // err: error trace
    // vm: component in which error occurred
    // info: vue specific error information such as lifecycle hooks, events etc.
    if (reportWarningOnly) {
      console.log('[ym:component] Unexpected exception in component:', vm, 'info', info)
    } else {
      console.error('[ym:component] Unexpected exception in component:', vm, 'info', info)
      const trace = getComponentTrace(vm)
      const limit = 50
      trace.slice(0, limit).forEach(t => console.error(`[ym:component] ${t}`))
      if (trace.length > limit) {
        console.error(`[ym:component] First ${limit} components of ${trace.length - limit} total shown`)
      }
    }

    // Show this error?
    const showError = error instanceof Error && !isSuppressedError(error)

    // We handle errors through central handler.
    if (showError) {
      errorHandler.thrown(error, 'Unexpected exception in component.')
    } else if (reportWarningOnly) {
      console.log(error)
    } else {
      console.error(error)
    }
  }
  nuxtApp.vueApp.config.errorHandler = appErrorHandler
  nuxtApp.hook('vue:error', appErrorHandler)

  nuxtApp.hook('apollo:error', async (errorResponse, ...args) => {
    let message = 'GraphQL Error'
    let stack
    if (errorResponse.graphQLErrors && errorResponse.graphQLErrors.length) {
      message = errorResponse.graphQLErrors[0].message
      stack = errorResponse.graphQLErrors[0].stack || errorResponse.graphQLErrors[0].extensions?.stacktrace
      if (Array.isArray(stack)) {
        stack = stack.join('\n')
      }
    }
    const error = new Error(message)
    if (stack) {
      error.stack = stack
    }
    const silent = errorResponse.operation.variables?.silent === true
    if (!silent) {
      await useDialogs().component('Error', 'Close', ErrorReport, { error, message: 'Unexpected server error', args }, null, { dataTestId: 'response-error' })
    }
  })
})
